import { getContrastRatio } from "./ContrastCalculator";

export const A11Y_RULES = {
  A11Y_MISSING_ALT: "A11Y_MISSING_ALT",
  A11Y_LOW_CONTRAST: "A11Y_LOW_CONTRAST",
  A11Y_EMPTY_BLOCK: "A11Y_EMPTY_BLOCK",
  A11Y_INCOMPLETE_QUIZ: "A11Y_INCOMPLETE_QUIZ",
  A11Y_VIDEO_NO_TRANSCRIPT: "A11Y_VIDEO_NO_TRANSCRIPT",
  A11Y_AUDIO_NO_TRANSCRIPT: "A11Y_AUDIO_NO_TRANSCRIPT",
};

export const inspectSectionAudio = (section, sectionIndex) => {
  if (section?.audioTrack?.url && (!section.audioTrack.transcript || !section.audioTrack.transcript.trim())) {
    return [
      {
        id: `audio-${sectionIndex}`,
        ruleId: A11Y_RULES.A11Y_AUDIO_NO_TRANSCRIPT,
        severity: "critical",
        wcagCriterion: "1.2.2",
        title: "Lydspor mangler transkripsjon",
        description: "Lydsporet i seksjonen mangler tekstlig transkripsjon for hørselshemmede.",
        target: { sectionIndex },
      },
    ];
  }
  return [];
};

export const inspectImageBlock = (block, target) => {
  if (block?.imagePath) {
    if (block.isDecorative) {
      return [];
    }
    if (!block.altText || !block.altText.trim()) {
      return [
        {
          id: `alt-${target.sectionIndex}-${target.containerIndex}-${target.blockIndex}`,
          ruleId: A11Y_RULES.A11Y_MISSING_ALT,
          severity: "critical",
          wcagCriterion: "1.1.1",
          title: "Bilde mangler alternativ tekst",
          description: "Legg til en beskrivende tekst for synshemmede.",
          target,
        },
      ];
    }
    return [];
  }
  return [
    {
      id: `empty-image-${target.sectionIndex}-${target.containerIndex}-${target.blockIndex}`,
      ruleId: A11Y_RULES.A11Y_EMPTY_BLOCK,
      severity: "warning",
      wcagCriterion: "4.1.2",
      title: "Tom bildeblokk",
      description: "Bildeblokken mangler valgt bilde.",
      target,
    },
  ];
};

const LARGE_FONT_TOKENS = new Set([
  "2XL", "3XL", "4XL", "5XL", "6XL", "7XL", "8XL", "9XL",
  "SIZE_2XL", "SIZE_3XL", "SIZE_4XL", "SIZE_5XL", "SIZE_6XL", "SIZE_7XL", "SIZE_8XL", "SIZE_9XL",
  "SIZE_XL2", "SIZE_XL3", "SIZE_XL4", "SIZE_XL5", "SIZE_XL6", "SIZE_XL7", "SIZE_XL8", "SIZE_XL9",
  "FONT_SIZE_24", "FONT_SIZE_30", "FONT_SIZE_36", "FONT_SIZE_48", "FONT_SIZE_60", "FONT_SIZE_72", "FONT_SIZE_92",
  "FONTSIZE-24", "FONTSIZE-30", "FONTSIZE-36", "FONTSIZE-48", "FONTSIZE-60", "FONTSIZE-72", "FONTSIZE-92",
  "FONTSIZE-24PX", "FONTSIZE-30PX", "FONTSIZE-36PX", "FONTSIZE-48PX", "FONTSIZE-60PX", "FONTSIZE-72PX", "FONTSIZE-92PX",
]);

const isLargeFontToken = (token) => {
  if (typeof token !== "string") return false;
  const upper = token.toUpperCase();
  if (LARGE_FONT_TOKENS.has(upper)) return true;
  return /(?:SIZE_X?L[2-9]|FONT_SIZE_(?:2[4-9]|[3-9]\d)|fontsize-(?:2[4-9]|[3-9]\d)|[2-9]xl)/i.test(token);
};

const parseDraftJson = (rawText) => {
  if (!rawText) return null;
  if (typeof rawText === "object") return rawText;
  if (typeof rawText !== "string") return null;
  try {
    return JSON.parse(rawText);
  } catch {
    return null;
  }
};

const extractPlainText = (rawText) => {
  const parsed = parseDraftJson(rawText);
  if (parsed && Array.isArray(parsed.blocks)) {
    return parsed.blocks.map((b) => b.text || "").join(" ");
  }
  return typeof rawText === "string" ? rawText : "";
};

const extractDraftStyles = (rawText) => {
  const result = { textColor: null, bgColor: null, hasLargeFont: false };
  const parsed = parseDraftJson(rawText);
  if (!parsed?.blocks || !Array.isArray(parsed.blocks)) {
    return result;
  }

  for (const b of parsed.blocks) {
    if (!Array.isArray(b.inlineStyleRanges)) continue;
    for (const range of b.inlineStyleRanges) {
      const style = range?.style;
      if (typeof style !== "string") continue;

      if (!result.textColor && style.startsWith("color-")) {
        result.textColor = style.slice(6);
      }
      if (!result.bgColor && style.startsWith("bgcolor-")) {
        const bgVal = style.slice(8);
        if (bgVal && bgVal !== "transparent") {
          result.bgColor = bgVal;
        }
      }
      if (!result.hasLargeFont && isLargeFontToken(style)) {
        result.hasLargeFont = true;
      }
    }
  }

  return result;
};

const resolveEffectiveColors = (block, container, section, draftStyles) => {
  const isMap = section?.mediaType === "map";
  const defaultText = isMap ? "#FFFFFF" : "#000000";
  const textColor = draftStyles.textColor || block?.color || defaultText;

  const defaultContainer = isMap ? "rgba(15, 23, 42, 0.88)" : undefined;
  const containerBg = container?.backgroundColor || defaultContainer;
  const bgColor = draftStyles.bgColor || containerBg || section?.backgroundColor || "#FFFFFF";

  const effectiveFallbackBg = section?.backgroundColor || "#808080";

  return { textColor, bgColor, effectiveFallbackBg };
};

export const inspectTextBlock = (block, container, section, target) => {
  const plainText = extractPlainText(block?.text);
  if (!plainText || !plainText.trim()) {
    return [
      {
        id: `empty-text-${target.sectionIndex}-${target.containerIndex}-${target.blockIndex}`,
        ruleId: A11Y_RULES.A11Y_EMPTY_BLOCK,
        severity: "warning",
        wcagCriterion: "1.3.1",
        title: "Tom tekstblokk",
        description: "Tekstblokken mangler innhold.",
        target,
      },
    ];
  }

  const draftStyles = extractDraftStyles(block?.text);
  const { textColor, bgColor, effectiveFallbackBg } = resolveEffectiveColors(
    block,
    container,
    section,
    draftStyles
  );

  const isLarge = Boolean(draftStyles.hasLargeFont || isLargeFontToken(block?.fontSize));
  const threshold = isLarge ? 3.0 : 4.5;
  const ratio = getContrastRatio(textColor, bgColor, effectiveFallbackBg);

  if (ratio < threshold) {
    return [
      {
        id: `contrast-${target.sectionIndex}-${target.containerIndex}-${target.blockIndex}`,
        ruleId: A11Y_RULES.A11Y_LOW_CONTRAST,
        severity: "warning",
        wcagCriterion: "1.4.3",
        title: "Tekst har for lav kontrast",
        description: `Kontrasten er ${ratio}:1. Kravet er minimum ${threshold}:1.`,
        target,
      },
    ];
  }

  return [];
};

export const inspectQuizBlock = (block, target) => {
  const hasQuestion = Boolean(block?.question && block.question.trim());
  const hasEnoughAlternatives = Array.isArray(block?.alternatives) && block.alternatives.length >= 2;
  const hasAnswer = Boolean(block?.answer);

  if (!hasQuestion || !hasEnoughAlternatives || !hasAnswer) {
    return [
      {
        id: `quiz-${target.sectionIndex}-${target.containerIndex}-${target.blockIndex}`,
        ruleId: A11Y_RULES.A11Y_INCOMPLETE_QUIZ,
        severity: "warning",
        wcagCriterion: "3.3.2",
        title: "Ufullstendig quiz",
        description: "Quiz må ha spørsmål, minst to alternativer og et fasitsvar.",
        target,
      },
    ];
  }

  return [];
};

export const inspectVideoBlock = (block, target) => {
  if (block?.videoUrl) {
    if (block.noSpeech) {
      return [];
    }
    const hasCaption = Boolean(block.caption && block.caption.trim());
    const hasTranscript = Boolean(block.transcript && block.transcript.trim());
    const hasTranscriptUrl = Boolean(block.transcriptUrl && block.transcriptUrl.trim());

    if (!hasCaption && !hasTranscript && !hasTranscriptUrl) {
      return [
        {
          id: `video-${target.sectionIndex}-${target.containerIndex}-${target.blockIndex}`,
          ruleId: A11Y_RULES.A11Y_VIDEO_NO_TRANSCRIPT,
          severity: "critical",
          wcagCriterion: "1.2.2",
          title: "Video mangler teksting eller transkripsjon",
          description:
            "Legg til transkripsjon på videoblokken, eller slå på «Ingen tale» i videomenyen hvis videoen ikke har tale.",
          target,
        },
      ];
    }
  }
  return [];
};

export const inspectStory = (story) => {
  if (!story || !Array.isArray(story.sections) || story.sections.length === 0) {
    return {
      issues: [],
      criticalCount: 0,
      warningCount: 0,
      isPassing: true,
    };
  }

  const issues = [];

  story.sections.forEach((section, sectionIndex) => {
    issues.push(...inspectSectionAudio(section, sectionIndex));

    (section.containers || []).forEach((container, containerIndex) => {
      (container.blocks || []).forEach((block, blockIndex) => {
        const target = { sectionIndex, containerIndex, blockIndex };

        if (block.type === "image") {
          issues.push(...inspectImageBlock(block, target));
        } else if (block.type === "text") {
          issues.push(...inspectTextBlock(block, container, section, target));
        } else if (block.type === "quiz") {
          issues.push(...inspectQuizBlock(block, target));
        } else if (block.type === "video") {
          issues.push(...inspectVideoBlock(block, target));
        }
      });
    });
  });

  const criticalCount = issues.filter((i) => i.severity === "critical").length;
  const warningCount = issues.filter((i) => i.severity === "warning").length;

  return {
    issues,
    criticalCount,
    warningCount,
    isPassing: issues.length === 0,
  };
};
